import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  User, Mail, Phone, Shield, Eye, EyeOff, CheckCircle2, AlertCircle,
  KeyRound, LogOut, BadgeCheck, Hash, ShieldCheck, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, API, formatApiError } from "../contexts/AuthContext";
import { Link } from "react-router-dom";
import "../lms.css";

/**
 * Shared Settings page — mounted at both /student/settings and /admin/settings.
 * Two cards: Profile info (read-only identity + contact) and Change password
 * (validated, double-confirm, with strength rules). Bottom section surfaces
 * session actions — logout + copy own user id (support escalations).
 */
export default function SettingsPage() {
  const { user, logout, refreshMe } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return <div className="max-w-3xl mx-auto p-10" style={{ color: "var(--px-ink-soft)" }}>Loading profile…</div>;
  }

  const roleLabel = user.role === "admin" ? "Administrator" : user.role === "mentor" ? "Mentor" : "Logician";

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-8 md:py-10">
      <div className="portal-page-header">
        <div>
          <div className="portal-kicker">— Settings</div>
          <h1 className="portal-page-title">Your account</h1>
          <p className="portal-page-sub">Manage your profile, password and session.</p>
        </div>
      </div>

      {/* Profile card */}
      <div className="lms-card p-5 md:p-6 mb-5" data-testid="settings-profile-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-full flex items-center justify-center text-lg font-semibold"
            style={{ background: "var(--accent-soft)", color: "var(--accent-deep)" }}>
            {(user.name || "?").charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="text-base font-bold" style={{ color: "var(--px-ink)" }}>{user.name || "—"}</div>
            <div className="text-xs mt-0.5 flex items-center gap-2" style={{ color: "var(--px-ink-mute)" }}>
              <BadgeCheck size={12} style={{ color: "var(--accent)" }} /> {roleLabel}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <InfoRow icon={Mail} label="Email" value={user.email} />
          <InfoRow icon={Phone} label="Phone" value={user.profile?.phone || "Not set"} />
          <InfoRow icon={User} label="Status" value={(user.status || "active").toUpperCase()} />
          <InfoRow icon={Hash} label="User ID" value={user.id} mono copyable />
        </div>
        {user.must_change_password && (
          <div className="mt-4 p-3 rounded-[10px] border text-xs flex items-start gap-2"
            style={{ borderColor: "var(--px-warn)", background: "var(--px-warn-soft)", color: "var(--px-warn)" }}
            data-testid="settings-pw-reminder">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <div>
              <strong style={{ color: "var(--px-ink)" }}>Password update required.</strong>{" "}
              <span style={{ color: "var(--px-ink-soft)" }}>You're still on your initial temp password. Change it below before it expires.</span>
            </div>
          </div>
        )}
      </div>

      {/* Change password card */}
      <ChangePasswordCard onChanged={async () => {
        await refreshMe();
        toast.success("Password updated. Please keep it safe.");
      }} />

      {/* Security & Active Devices link — only for students (admins don't have single-session) */}
      {user.role === "student" && (
        <Link
          to="/student/settings/security"
          className="lms-card p-5 md:p-6 mt-5 flex items-center gap-4 group hover:border-cyan-glow/40 transition-all"
          data-testid="settings-security-link"
          style={{ textDecoration: "none" }}
        >
          <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
               style={{ background: "rgba(92,238,255,0.12)", border: "1px solid rgba(92,238,255,0.28)", color: "#5ceeff" }}>
            <ShieldCheck size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-base font-semibold" style={{ color: "var(--px-ink)" }}>Security & active devices</div>
            <div className="text-[12px] mt-0.5" style={{ color: "var(--px-ink-mute)" }}>
              See where your account is signed in · Sign out everywhere
            </div>
          </div>
          <ChevronRight size={16} style={{ color: "var(--px-ink-mute)" }} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      )}

      {/* Session actions */}
      <div className="lms-card p-5 md:p-6 mt-5" data-testid="settings-session-card">
        <div className="portal-kicker mb-2">— Session</div>
        <div className="text-sm mb-3" style={{ color: "var(--px-ink-soft)" }}>
          Logging out clears your session cookies on this device. You'll need to sign in again to continue.
        </div>
        <button
          onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
          className="portal-btn h-10 text-sm inline-flex items-center"
          style={{ borderColor: "var(--px-danger)", color: "var(--px-danger)" }}
          data-testid="settings-logout-btn"
        >
          <LogOut size={13} /> Log out of this device
        </button>
      </div>
    </div>
  );
}

/* ───────── Sub-components ───────── */

function InfoRow({ icon: Icon, label, value, mono, copyable }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch { toast.error("Couldn't copy"); }
  };
  return (
    <div className="p-3 rounded-[10px] border flex items-center gap-3"
      style={{ borderColor: "var(--px-line)", background: "var(--px-paper-soft)" }}>
      <Icon size={14} style={{ color: "var(--px-ink-mute)" }} />
      <div className="flex-1 min-w-0">
        <div className="portal-kicker" style={{ marginBottom: 2 }}>— {label}</div>
        <div className="text-sm truncate" style={{ color: "var(--px-ink)", fontFamily: mono ? "var(--font-mono)" : undefined, fontSize: mono ? 12 : 14 }}>
          {value}
        </div>
      </div>
      {copyable && (
        <button onClick={copy} className="w-7 h-7 rounded-md hover:bg-[var(--px-paper)] text-[10px] font-semibold"
          style={{ color: "var(--px-ink-mute)" }}
          data-testid={`settings-copy-${label.toLowerCase().replace(/\s+/g, "-")}`}>
          COPY
        </button>
      )}
    </div>
  );
}

function ChangePasswordCard({ onChanged }) {
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const strength = pwStrength(pw);
  const strengthLabel = ["Too weak", "Weak", "Fair", "Strong", "Excellent"][strength];
  const strengthColor = ["var(--px-danger)", "var(--px-danger)", "var(--px-warn)", "var(--px-success)", "var(--px-success)"][strength];

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) return setErr("Password must be at least 8 characters.");
    if (strength < 2) return setErr("Password is too predictable. Mix letters, numbers and symbols.");
    if (pw !== pw2) return setErr("The two passwords don't match.");
    if (current && current === pw) return setErr("New password must be different from current.");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, { new_password: pw });
      setDone(true);
      setCurrent(""); setPw(""); setPw2("");
      onChanged?.();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lms-card p-5 md:p-6" data-testid="settings-password-card">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound size={15} style={{ color: "var(--accent)" }} />
        <div className="text-base font-bold" style={{ color: "var(--px-ink)" }}>Change password</div>
      </div>
      <div className="text-sm mb-4" style={{ color: "var(--px-ink-soft)" }}>
        Pick a strong, memorable password. Minimum 8 characters — we recommend a mix of letters, numbers and at least one symbol.
      </div>

      {done ? (
        <motion.div
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-[10px] border flex items-start gap-2"
          style={{ borderColor: "var(--px-success)", background: "var(--px-success-soft)" }}
          data-testid="settings-pw-success">
          <CheckCircle2 size={16} className="shrink-0 mt-0.5" style={{ color: "var(--px-success)" }} />
          <div>
            <div className="text-sm font-semibold" style={{ color: "var(--px-ink)" }}>Password updated</div>
            <div className="text-xs mt-1" style={{ color: "var(--px-ink-soft)" }}>
              Use your new password from the next login. You can change it again anytime.
            </div>
            <button
              onClick={() => setDone(false)}
              className="portal-btn portal-btn-ghost text-xs mt-3"
              data-testid="settings-pw-change-again"
            >Change again</button>
          </div>
        </motion.div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="settings-pw-form">
          <PwInput
            label="New password"
            value={pw}
            onChange={setPw}
            show={show}
            onToggleShow={() => setShow(v => !v)}
            testid="settings-pw-new"
            autoFocus
          />
          {pw && (
            <div className="flex items-center gap-2" data-testid="settings-pw-strength">
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--px-line)" }}>
                <div style={{ width: `${(strength + 1) * 20}%`, height: "100%", background: strengthColor, transition: "width 200ms" }} />
              </div>
              <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: strengthColor }}>
                {strengthLabel}
              </div>
            </div>
          )}
          <PwInput
            label="Confirm new password"
            value={pw2}
            onChange={setPw2}
            show={show}
            onToggleShow={() => setShow(v => !v)}
            testid="settings-pw-confirm"
          />

          {err && (
            <div className="p-2 rounded-[10px] border text-xs flex items-start gap-2"
              style={{ borderColor: "var(--px-danger)", background: "var(--px-danger-soft)", color: "var(--px-danger)" }}
              data-testid="settings-pw-error">
              <AlertCircle size={13} className="shrink-0 mt-0.5" /> <div>{err}</div>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={busy || !pw || !pw2}
              className="portal-btn portal-btn-primary h-10 text-sm"
              data-testid="settings-pw-submit"
            >
              {busy ? "Updating…" : <><Shield size={13} /> Update password</>}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function PwInput({ label, value, onChange, show, onToggleShow, testid, autoFocus }) {
  return (
    <label className="block">
      <div className="portal-label">{label}</div>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="new-password"
          autoFocus={autoFocus}
          className="w-full rounded-[10px] px-3 py-2.5 text-sm outline-none pr-10 transition"
          style={{
            color: "#0b0b0d",
            background: "#f4f0ea",
            border: "1.5px solid var(--px-line-strong)",
            fontFamily: "var(--font-body)",
          }}
          onFocus={(e) => { e.target.style.borderColor = "var(--accent)"; e.target.style.background = "#ffffff"; }}
          onBlur={(e) => { e.target.style.borderColor = "var(--px-line-strong)"; e.target.style.background = "#f4f0ea"; }}
          data-testid={testid}
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md flex items-center justify-center hover:bg-[var(--px-paper-soft)]"
          style={{ color: "var(--px-ink-mute)" }}
          aria-label={show ? "Hide password" : "Show password"}
          tabIndex={-1}
        >
          {show ? <EyeOff size={13} /> : <Eye size={13} />}
        </button>
      </div>
    </label>
  );
}

/** 0..4 — simple entropy-ish scoring good enough for UX feedback. */
function pwStrength(pw) {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(score, 4);
}
